<template>
  <b-card class="h-100">
    <h5 class="font-weight-bold">Upload Details</h5>
    <b-card-body class="p-0 align-items-center h-100">
      <b-list-group flush class="h-100 list">
        <b-list-group-item v-if="!attributionSamePerson"
          ><b-row class="font-weight-bold text-secondary small"
            >Created by</b-row
          >
          <b-row>{{ createdByLabel }}</b-row></b-list-group-item
        >
        <b-list-group-item
          ><b-row class="font-weight-bold">{{
            attributionSamePerson ? 'Created & owned by' : 'Owned by'
          }}</b-row>
          <b-row
            ><b-col cols="auto" class="px-0 pt-1 avatar-col"
              ><b-avatar
                button
                v-if="userAvatar"
                variant="light"
                :src="userAvatar"
              ></b-avatar>
              <b-avatar
                button
                v-else
                variant="primary"
                :text="userInitials"
              ></b-avatar></b-col
            ><b-col style="word-break: break-word"
              ><b-row class="font-weight-bold"
                >{{ file.user ? file.user.full_name : '' }}
                {{ file.user && file.user.mine ? '(You)' : '' }}</b-row
              ><b-row>{{ file.user && file.user.email }}</b-row></b-col
            >
          </b-row></b-list-group-item
        >
        <b-list-group-item>
          <b-row class="font-weight-bold">Visibility</b-row>
          <b-row>{{ visibilityDisplay }}</b-row>
        </b-list-group-item>
        <b-list-group-item v-show="isShareable && !isBusinessAdminUser">
          <b-row class="font-weight-bold">Shared to</b-row>
          <b-row>
            <b-button
              v-if="fileSharesCount"
              variant="link"
              class="btn text-dark text-decoration-none btn-link p-0 h-auto"
              @click="openShareFile"
            >
              <span class="mr-2 align-middle">
                <b-icon scale="1" icon="eye" aria-hidden="true"></b-icon>
              </span>
              {{ fileSharesCount + ' people' }}
            </b-button>
            <div v-else>
              <span class="mr-2">
                <b-icon scale="1" icon="eye-slash" aria-hidden="true"></b-icon>
              </span>
              None
            </div>
          </b-row>
        </b-list-group-item>
        <b-list-group-item v-if="!isBusinessAdminUser">
          <b-row class="font-weight-bold">TradiePass Verification</b-row>
          <b-row>
            <b-badge
              pill
              :variant="statusBadge(fileStatus)"
              class="badge-hovered text-capitalize"
            >
              {{ fileStatus }}
            </b-badge>
            <span
              v-if="file.template_id && showLicenceProvidersLink"
              class="pt-1 pl-2"
            >
              <b-link
                active
                :to="{
                  name: 'provider.list-page',
                  params: { id: file.template_id },
                }"
                ><u>Browse for licence providers</u></b-link
              >
            </span>
          </b-row>
        </b-list-group-item>
        <b-list-group-item v-if="fileStatus === 'Rejected'">
          <b-row class="font-weight-bold">Reason</b-row>
          <b-row
            ><span v-if="file.file_rejects.length > 0">{{
              file.file_rejects[0].reason
            }}</span>
          </b-row>
        </b-list-group-item>
        <b-list-group-item
          ><b-row class="font-weight-bold">Uploaded</b-row>
          <b-row>{{ uploaded }}</b-row></b-list-group-item
        >
        <b-list-group-item v-if="$slots.approval" class="approval-slot">
          <slot name="approval" />
        </b-list-group-item>
      </b-list-group>
    </b-card-body>
  </b-card>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  getCreatedByLabel,
  isSameCreatorAndOwner,
  formatFileVisibilityDisplay,
} from '@/utils/fileLabels'
export default {
  props: {
    file: {
      type: Object,
      required: true,
    },
    /** Set false on file view page to hide the providers link there only. */
    showLicenceProvidersLink: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    ...mapGetters('auth', ['user']),
    createdByLabel() {
      return getCreatedByLabel(this.file, (id) => this.resolveCreatorName(id))
    },
    attributionSamePerson() {
      return isSameCreatorAndOwner(this.file, (id) =>
        this.resolveCreatorName(id),
      )
    },
    visibilityDisplay() {
      return formatFileVisibilityDisplay(this.file)
    },
    isBusinessAdminUser() {
      return this.user ? this.user.user_type === 'business_admin' : false
    },
    userAvatar() {
      return this.file.user && this.file.user.avatar
        ? this.file.user.avatar_permanent_url
        : ''
    },
    userInitials() {
      return this.file.user && this.file.user.full_name
        ? this.file.user.full_name
            .match(/(^\S\S?|\b\S)?/g)
            .join('')
            .match(/(^\S|\S$)?/g)
            .join('')
            .toUpperCase()
        : ''
    },
    uploaded() {
      return this.getTimeDiff(this.file.updated_at)
    },
    fileSharesCount() {
      return (this.file.file_shares || []).length
    },
    fileStatus() {
      return this.file.status
        .trim()
        .toLowerCase()
        .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
    },
    isShareable() {
      return (
        this.file.status === 'valid' ||
        this.file.status === 'not started' ||
        this.file.status === 'expiring soon'
      )
    },
  },

  methods: {
    resolveCreatorName(id) {
      if (this.user && this.user.id === id) {
        return (this.user.full_name || this.user.email || '').trim() || null
      }
      const u =
        (this.file && this.file.user) ||
        (this.file && this.file.file && this.file.file.user)
      if (u && Number(u.id) === Number(id)) {
        return (u.full_name || u.email || '').trim() || null
      }
      return null
    },
    openShareFile() {
      return this.$emit('openShareFile', this.file.file_shares)
    },
    statusBadge(status) {
      return status === 'Valid'
        ? 'success'
        : status === 'Pending Approval'
        ? 'warning'
        : status === 'Rejected'
        ? 'danger'
        : 'secondary'
    },
  },
}
</script>

<style lang="scss" scoped>
// Was `cols="3" md="2" lg="2"` — a percentage-wide column can end up
// narrower than the avatar's own fixed size (this card is the narrow
// sidebar column at md, ~768px), squeezing the avatar horizontally into an
// oval instead of a circle. `cols="auto"` sizes the column to the avatar's
// own content width at every breakpoint so it's never squeezed.
.avatar-col {
  flex-shrink: 0;
}

// At md and up (this card is the narrower sidebar column) the avatar and
// the name/email column sit right on top of each other with only the
// grid's default gutter between them — add a bit of breathing room from
// md onward, all the way through normal desktop sizes. Phones keep their
// own tightened layout below instead (avatar shrinks there, so this
// extra margin isn't needed).
@media (min-width: 768px) {
  .avatar-col {
    margin-right: 0.5rem;
  }
}

// flex-shrink:0 on the column alone isn't enough — bootstrap-vue's own
// avatar sizing/the inner photo's own aspect ratio can still squish it.
// Same fix as MainLayout.vue's navbar avatar: pin explicit circle
// dimensions on the outer span and force the inner image to absolutely
// fill + cover it instead of stretching to its own aspect ratio.
/deep/ .b-avatar {
  flex: 0 0 auto;
  width: 40px !important;
  height: 40px !important;
  position: relative;
  overflow: hidden;
  border-radius: 50%;
}

/deep/ .b-avatar .b-avatar-img {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  display: block !important;
  border-radius: 50%;
  overflow: hidden;
}

/deep/ .b-avatar .b-avatar-img img,
/deep/ .b-avatar > img {
  display: block;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  border-radius: inherit;
}

.badge-hovered {
  font-size: 15px;
  font-weight: 700;
  padding: 5px 20px 5px 20px;
  background: #dadde2 0% 0% no-repeat padding-box;
  color: #3f4858 !important;

  &.badge-success {
    background: #d1ffef 0% 0% no-repeat padding-box !important;
    color: #00d392 !important;
  }

  &.badge-danger {
    background: #ffe2e2 0% 0% no-repeat padding-box !important;
    color: #e52c00 !important;
  }

  &.badge-warning {
    background: #fff3cc 0% 0% no-repeat padding-box !important;
    color: #a07602 !important;
  }
}

// Small-phone-only tightening. Scoped inside this media query so nothing
// changes at 576px and up.
@media (max-width: 575px) {
  h5 {
    font-size: 0.95rem;
  }

  .list.h-100 .row {
    font-size: 0.8rem;
  }

  .b-avatar {
    width: 2rem !important;
    height: 2rem !important;
  }

  .badge-hovered {
    font-size: 12px;
    padding: 4px 14px;
  }
}
</style>
